/* eslint-disable */
import { useState, useRef, useEffect } from "react";
import { C } from "../shared/globals.js";

// ─── helpers ───────────────────────────────────────────────────────────────
const fmt$ = v =>
  v >= 1000 ? "$" + (v / 1000).toFixed(1) + "k" : "$" + Math.round(v);

const fmtMonth = m => {
  const [y, mo] = m.split("-");
  const names = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  return names[parseInt(mo) - 1] + " '" + y.slice(2);
};

const fmtWeek = weekKey => {
  const d = new Date(weekKey);
  const mo = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][d.getMonth()];
  return String(d.getDate()).padStart(2, "0") + " " + mo;
};

// ─── Single Lollipop ───────────────────────────────────────────────────────
function LollipopChart({ items, getKey, getLabel, getValue, color }) {
  const wrapRef = useRef(null);
  const [w, setW] = useState(400);
  const [hoveredKey, setHoveredKey] = useState(null);

  useEffect(() => {
    const obs = new ResizeObserver(es => {
      setW(Math.max(es[0].contentRect.width, 200));
    });
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setW(width);
        }
      });
    }
    return () => obs.disconnect();
  }, []);

  if (!items.length) return null;

  const CHART_H = 200;
  const minR = 6, maxR = 26;
  const LIFT  = 18;
  const MIN_STEM = 10;
  const PAD_B = 36;
  const PAD_T = maxR + LIFT + 24;
  const barH   = CHART_H - PAD_B - PAD_T;
  const baseline = CHART_H - PAD_B;

  const values   = items.map(it => getValue(it));
  const maxVal   = Math.max(...values, 1);
  const getR     = v => minR + ((v / maxVal) ** 0.5) * (maxR - minR);

  // dynamic spacing — try to fit all items, min 44px each
  const minStep = 44;
  const totalW  = Math.max(items.length * minStep, w);
  const step    = totalW / items.length;

  return (
    <div ref={wrapRef} style={{ overflowX: "auto", width: "100%" }}>
      <svg
        width="100%"
        height={CHART_H}
        viewBox={`0 0 ${totalW} ${CHART_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", minWidth: items.length * minStep }}
      >
        {/* Baseline */}
        <line
          x1={0} y1={baseline}
          x2={totalW} y2={baseline}
          stroke="#444" strokeWidth={1} strokeOpacity={0.4}
        />

        {items.map((it, i) => {
          const val = getValue(it);
          const r   = getR(val);
          const cx  = step * 0.5 + i * step;
          const naturalY = PAD_T + (1 - val / maxVal) * barH;
          const cy  = naturalY - LIFT;
          const stemTop = Math.min(cy + r, baseline - MIN_STEM);

          return (
            <g
              key={getKey(it)}
              onMouseEnter={() => setHoveredKey(getKey(it))}
              onMouseLeave={() => setHoveredKey(null)}
              style={{
                transform: hoveredKey === getKey(it) ? `translateY(-6px)` : "translateY(0px)",
                transition: "transform 0.22s cubic-bezier(0.34,1.4,0.64,1)",
              }}
            >
              {/* Stem */}
              <line
                x1={cx} y1={stemTop}
                x2={cx} y2={baseline}
                stroke={color}
                strokeWidth={hoveredKey === getKey(it) ? 2 : 1.5}
                strokeOpacity={hoveredKey === getKey(it) ? 1 : 0.85}
                style={{ transition: "stroke-width 0.15s, stroke-opacity 0.15s" }}
              />
              {/* Ball */}
              <circle
                cx={cx} cy={cy} r={hoveredKey === getKey(it) ? r * 1.18 : r}
                fill={color}
                style={{ transition: "r 0.18s cubic-bezier(0.34,1.4,0.64,1)", filter: hoveredKey === getKey(it) ? `drop-shadow(0 0 6px ${color})` : "none" }}
              />
              {/* Amount above ball */}
              <text
                x={cx} y={cy - r - 5}
                textAnchor="middle"
                style={{
                  fontFamily: "Montserrat,sans-serif",
                  fontSize: 9.6,
                  fontWeight: 700,
                  fill: "#fff",
                }}
              >
                {fmt$(val)}
              </text>
              {/* Label below baseline */}
              <text
                x={cx} y={baseline + 14}
                textAnchor="middle"
                style={{
                  fontFamily: "Montserrat,sans-serif",
                  fontSize: 8,
                  fontWeight: 700,
                  fill: "var(--text4)",
                  letterSpacing: "0.04em",
                }}
              >
                {getLabel(it)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Main export ────────────────────────────────────────────────────────────
// monthlyData : [{ month:"YYYY-MM", dollars, shares }]
// weeklyData  : [{ week:"YYYY-MM-DD", dollars, shares }]
export default function MonthlyWeeklyLollipop({ monthlyData = [], weeklyData = [] }) {
  if (!monthlyData.length && !weeklyData.length) return null;

  return (
    <div style={{
      display: "flex",
      flexDirection: "row",
      gap: 12,
      alignItems: "stretch",
    }}>
      {/* ── Monthly ── */}
      <div style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "16px 16px 8px",
      }}>
        <div style={{
          fontFamily: "Montserrat,sans-serif",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.25em",
          color: "var(--text4)",
          marginBottom: 12,
        }}>
          MONTHLY INVESTED
        </div>
        <LollipopChart
          items={monthlyData}
          getKey={d => d.month}
          getLabel={d => fmtMonth(d.month)}
          getValue={d => d.dollars}
          color={C.orange}
        />
      </div>

      {/* ── Weekly ── */}
      <div style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "16px 16px 8px",
      }}>
        <div style={{
          fontFamily: "Montserrat,sans-serif",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.25em",
          color: "var(--text4)",
          marginBottom: 12,
        }}>
          WEEKLY INVESTED
        </div>
        <LollipopChart
          items={weeklyData}
          getKey={d => d.week}
          getLabel={d => fmtWeek(d.week)}
          getValue={d => d.dollars}
          color="#60a5fa"
        />
      </div>
    </div>
  );
}
